import { useCallback } from 'react';

import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import IsMarkdownModeQuery from './__generated__/IsMarkdownModeQuery.graphql.ts';
import type {
	IsMarkdownModeQuery$data,
	IsMarkdownModeQuery$variables,
} from './__generated__/IsMarkdownModeQuery.graphql.ts';
import {
	default as UpdateMarkdownModeMutation,
	type UpdateMarkdownModeMutation$data,
	type UpdateMarkdownModeMutation$variables,
} from './__generated__/UpdateMarkdownModeMutation.graphql.ts';

export type UseUpdateMarkdownModeResult = {
	updateMarkdownMode: (
		contentId: string,
		isMarkdownMode: boolean,
	) => Promise<{
		success: boolean;
		errorMessage?: string;
	}>;
	loading: boolean;
	error: Error | undefined | null;
};

/**
 * Patch the cached `IsMarkdownModeQuery` for `contentId`. The patch
 * triggers a re-render → preset rebuild → `reconfigureState`, which
 * migrates the PM doc into the new schema; callers MUST replace the doc
 * with the markdown-safe body first, otherwise the migration throws on
 * rich-text-only nodes (`status`, `panel`, `expand`, ...) and resets the
 * editor to empty. See `flipFlagToMarkdown`.
 *
 * `isMarkdownModeEnabled` must match the value the consuming
 * `useIsMarkdownMode` query was issued with, otherwise the cache key
 * won't match. Pass an inline `expValEquals('cc-markdown-mode',
 * 'isEnabled', true)` from the callsite to keep the experiment usage
 * statically analysable.
 *
 * Cache miss is a no-op; `useIsMarkdownMode` will fetch fresh on next
 * render.
 */
export const patchIsMarkdownModeCache = (
	contentId: string,
	isMarkdownMode: boolean,
	isMarkdownModeEnabled: boolean,
) => {
	const variables: IsMarkdownModeQuery$variables = {
		contentId,
		isMarkdownModeEnabled,
	};

	try {
		const cached = readQueryInterop<IsMarkdownModeQuery$data, IsMarkdownModeQuery$variables>({
			query: IsMarkdownModeQuery,
			variables,
		});

		const updatedNodes = (cached?.content?.nodes ?? []).map((node) => {
			if (!node || node.id !== contentId) {
				return node;
			}
			return {
				...node,
				contentProperties: {
					...node.contentProperties,
					latest: {
						...node.contentProperties?.latest,
						isMarkdownMode,
					},
				},
			};
		});

		writeQueryInterop<IsMarkdownModeQuery$data, IsMarkdownModeQuery$variables>({
			query: IsMarkdownModeQuery,
			variables,
			data: {
				content: {
					...cached?.content,
					nodes: updatedNodes,
				},
			},
		});
	} catch {
		// Cache miss — see JSDoc.
	}
};

/**
 * Toggle a live page between markdown and rich-text modes. Returns a
 * normalised `{ success, errorMessage }` so callers don't dig through the
 * mutation payload.
 *
 * Does NOT auto-patch `IsMarkdownModeQuery`. Callers invoke
 * `patchIsMarkdownModeCache` themselves AFTER replacing the PM doc with
 * the converted body — see `flipFlagToMarkdown`.
 */
export const useUpdateMarkdownMode = (): UseUpdateMarkdownModeResult => {
	const { cloudId } = useSessionData();

	const [updateMarkdownModeMutation, { loading, error }] = useMutationInterop<
		UpdateMarkdownModeMutation$data,
		UpdateMarkdownModeMutation$variables
	>(UpdateMarkdownModeMutation);

	const updateMarkdownMode = useCallback(
		async (contentId: string, isMarkdownMode: boolean) => {
			try {
				const result = await updateMarkdownModeMutation({
					variables: {
						cloudId,
						input: {
							contentId,
							isMarkdownMode,
						},
					},
				});

				const payload = result?.data?.confluence_updateMarkdownMode;
				if (payload?.success) {
					return { success: true };
				}

				const errorMessage = payload?.errors?.[0]?.message ?? undefined;
				return { success: false, errorMessage };
			} catch (e) {
				const errorMessage = e instanceof Error ? e.message : undefined;
				return { success: false as const, errorMessage };
			}
		},
		[cloudId, updateMarkdownModeMutation],
	);

	return { updateMarkdownMode, loading, error };
};
